import { styled } from 'styled-components';
import type { CSSProperties, Dispatch, SetStateAction, JSX, ComponentPropsWithRef } from 'react';
import { useEffect, useRef, useState } from 'react';
import { isNotNil } from '~/utilities/type-guards';

const Tabs = styled.div`
  --tab-text-color: ${({ theme }) => theme.color.grey900};
  --tab-border-color: ${({ theme }) => theme.color.grey300};
  --tab-hover-color: ${({ theme }) => theme.color.brandBlue400};
  --tab-active-color: ${({ theme }) => theme.color.brandBlue500};
  --tab-underline-width: 4px;
  --tab-border-width: 1px;

  display: flex;
  justify-content: flex-start;
  min-height: 34px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: calc(var(--tab-underline-width) + var(--tab-border-width));
  position: relative;
  width: 100%;
  border-bottom: var(--tab-border-width) solid var(--tab-border-color);

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Underline = styled.div<{ offsetLeft: string; width: string }>`
  background: var(--tab-active-color);
  border-radius: 2px;
  bottom: -1px;
  height: var(--tab-underline-width);
  min-height: var(--tab-underline-width);
  position: absolute;
  transition:
    width 150ms ease-in-out,
    transform 150ms ease-in-out;
  z-index: 2;
  ${({ offsetLeft, width }) => `
    transform: translateX(${offsetLeft});
    width: ${width};
  `}
`;

const TabButton = styled.button`
  background-color: transparent;
  border: none;
  color: var(--tab-text-color);
  cursor: pointer;
  font-weight: ${({ theme }) => theme.font.weight.medium};
  line-height: 24px;
  min-height: 30px;
  outline: none;
  padding: 0 12px;

  div:first-child {
    width: max-content;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    border-radius: 4px;
    color: var(--tab-active-color);
    outline: 2px solid var(--tab-active-color);
    z-index: 2;
  }

  &:hover {
    color: var(--tab-hover-color);
  }

  &[data-active='true'] {
    color: var(--tab-active-color);
  }
`;

export type TabBarProps<Tab extends number | string | null> = ComponentPropsWithRef<'div'> & {
  tabs: Tab[];
  activeTab: Extract<Tab, unknown> | null;
  onTabClick: Dispatch<SetStateAction<Tab>>;
  style?: CSSProperties;
  guidanceOptions?: {
    tab: string;
    id: string;
  };
};

/**
 * TabBar component that displays a list of tabs with an underline under the active tab.
 *
 * @param tabs - List of tab names to display
 * @param activeTab - The tab that is currently active
 * @param onTabClick - Callback when a tab is clicked
 * @param className  - Supports styled-components className prop or any other class name
 */
export const TabBar = <Tab extends number | string | null>({
  tabs,
  activeTab,
  onTabClick,
  className,
  style,
  guidanceOptions,
}: TabBarProps<Tab>): JSX.Element => {
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const activeTabElem = tabRefs.current[activeTab as string];

  // We need to force a re-render after the refs are set so that the
  // blue underline appears under the initially selected tab.
  const [_, setStateToForceRenderWithRefsSet] = useState(false);
  useEffect(() => {
    setStateToForceRenderWithRefsSet(true);
  }, []);

  return (
    <Tabs
      className={className}
      style={style}
    >
      {tabs.map((tab) => {
        const commentsTabWithGuidance = tab === guidanceOptions?.tab;
        return (
          <TabButton
            key={tab}
            id={commentsTabWithGuidance ? guidanceOptions.id : ''}
            ref={(elem: HTMLButtonElement) => {
              tabRefs.current[tab as string] = elem;
            }}
            role="tab"
            aria-selected={activeTab === tab}
            data-active={activeTab === tab}
            data-testid={`tab-${tab}`}
            onClick={() => onTabClick(tab)}
          >
            {tab}
          </TabButton>
        );
      })}
      {isNotNil(activeTabElem) ? (
        <Underline
          offsetLeft={`${activeTabElem.offsetLeft}px`}
          width={`${activeTabElem.offsetWidth}px`}
        />
      ) : null}
    </Tabs>
  );
};
